import {useCallback} from 'react'
import BasketMgr from '../custom-api/basket-custom-api'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useMultiSite from './use-multi-site'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import useShipToCountry from '@app/hooks/use-ship-to-country'

import {getLocalStorageJSONItem} from '../utils/utils'

/**
 * Custom React hook for managing and accessing basket assignments within the application.
 * Typically used to retrieve, update, or manipulate assignment data related to the user's shopping basket.
 * @returns {Function} - A function to manually trigger basket assignment.
 */
const useBasketAssignments = () => {
    const {getTokenWhenReady} = useAccessToken()
    const {site, locale} = useMultiSite()
    const {shipToCountry} = useShipToCountry()

    const countryCodeStillSetting = getLocalStorageJSONItem('countryCode')
    const {refetch} = useCurrentBasket()

    const assignBasket = useCallback(
        async (isCartPage = false, toRefetch = true) => {
            if (shipToCountry?.id && !countryCodeStillSetting) {
                try {
                    const accessToken = await getTokenWhenReady()
                    const basketMgr = new BasketMgr({
                        parameters: {
                            siteId: site.id,
                            locale: locale.id
                        }
                    })
                    await basketMgr.sfsBasketAssignments(
                        accessToken,
                        site.id,
                        locale.id,
                        isCartPage
                    )
                    if (toRefetch) {
                        await refetch()
                    }
                } catch (error) {
                    console.error('Error assigning basket:', error)
                }
            }
        },
        [getTokenWhenReady, site.id, locale.id, shipToCountry?.id, countryCodeStillSetting]
    )

    return assignBasket
}

export default useBasketAssignments
